<template>
  <div :class="['modal-card has-modal-large', $style.modalContainer]">
    <header class="modal-card-head">
      <p class="modal-card-title">Export GRiP Report</p>
      <button @click="$emit('close')" type="button" class="close-icon">
        <i class="mdi mdi-close"></i>
      </button>
    </header>

    <section :class="['modal-card-body padding-16', $style.container]">
      <!-- DomainCategory required checkbox -->
      <div class="margin-top20 margin-bottom20">
        <h1 class="margin-bottom8">GRiP report</h1>
        <label class="b-checkbox checkbox">
          <input type="checkbox" v-model="isDomainCategoryRequired" />
          <span class="check"></span>
          <p class="margin-left12">
            Include domain/category for classification of
            barriers/enablers/strategies
          </p>
        </label>
      </div>

      <!-- select export type -->
      <div class="margin-top20 margin-bottom20">
        <h1 class="margin-bottom8">File Export</h1>
        <b-field label="Export Type">
          <b-dropdown
            aria-role="list"
            v-model="exportType"
            class="grip-export-type-dropdown-container"
          >
            <button
              class="button grip-export-type-dropdown"
              slot="trigger"
              @click="handleDropdown"
            >
              <span>
                {{ exportType ? exportType : 'Select export type' }}
              </span>
              <b-icon :icon="isDropdownActive ? 'menu-up' : 'menu-down'" />
            </button>
            <b-dropdown-item
              aria-role="listitem"
              v-for="(option, index) in exportOptions"
              :key="`${exportOptions + index}`"
              :value="option"
              @click="handleDropdown"
              class="grip-export-type-dropdown-item"
            >
              {{ option }}
            </b-dropdown-item>
          </b-dropdown>
        </b-field>
      </div>
      <!-- analysis list -->
      <div class="columns">
        <div class="column">
          <b-field
            class="grip-search-input-box"
            label="Select Analysis to export"
          >
            <b-input
              placeholder="Search for analysis"
              type="search"
              icon="magnify"
              icon-clickable
              v-model.trim="searchAnalysisTitle"
            >
            </b-input>
          </b-field>
        </div>
      </div>
      <p :class="$style.selectedCount">
        {{ `${selectedGripAnalysisIds.length} Selected Analysis` }}
      </p>
      <BaseLoading v-if="isLoading" />
      <BasePaginatorHoc
        v-else
        :component="ExportGripReportModalAnalysisList"
        :page.sync="page"
        :perPage.sync="perPage"
        :gripAnalysisItems="gripAnalysisList"
        :selectedGripAnalysisIds="selectedGripAnalysisIds"
        :totalCount="totalCount"
        :sortColumn="sortColumn"
        :sortOrder="sortOrder"
        @paginate="handlePaginator"
        @sort="(data) => handleSort(data)"
        @updateSelectedAnalysis="updateSelectedAnalysis"
      />
    </section>

    <footer class="modal-card-foot">
      <div class="columns level margin-none">
        <div class="level-left padding-horizontal-12"></div>

        <div class="level-right padding-horizontal-12">
          <button
            class="button is-large is-rounded is-primary bold margin-left24"
            @click="handleExport"
            :disabled="isButtonDisabled"
          >
            Export
          </button>
        </div>
      </div>
    </footer>
  </div>
</template>

<script lang="ts">
import { Component, Vue, Prop, Watch } from 'vue-property-decorator';
import { Action, State } from 'vuex-class';
import BaseLoading from '../../../components/base/BaseLoading.vue';
import BasePaginatorHoc from '../../../components/base/BasePaginatorHoc.vue';
import { Debounce } from '../../../jbi-shared/util/debounce.vue-decorator';
import { isDifferentDeep } from '../../../jbi-shared/util/watcher.vue-decorator';
import {
  ProjectCriteria,
  ProjectDetailPayload,
} from '../../../store/modules/projects/types/projects.types';
import {
  ApiState,
  Pagination,
  PaginatorDto,
  SortOrder,
  TableSortDto,
} from '../../../store/types/general.types';
import {
  GripAnalysis,
  GripAnalysisCriteria,
  GripAnalysisParams,
  GripExportReportPayload,
  GripExportReportResponse,
} from '../../../store/types/grip.types';
import ExportGripReportModalAnalysisList from './ExportGripReportModalAnalysisList.vue';
import { generateGripReportPdf } from '../../../components/grip/export/pdf/GripReportPdf';
import { generateGripReportDocx } from '../../../components/grip/export/docx/GripReportDocx';

@Component({
  components: {
    BaseLoading,
    BasePaginatorHoc,
    ExportGripReportModalAnalysisList,
  },
})
export default class ExportGripReportModal extends Vue {
  @Prop(Number) public projectId!: number;
  public isLoading: boolean = true;
  public isDropdownActive: boolean = false;
  public totalCount: number = 0;
  public perPage = 50;
  public page = 1;
  public sortColumn: string = 'title';
  public sortOrder: SortOrder = SortOrder.ASC;
  public searchAnalysisTitle = '';
  public filteredParams: GripAnalysisParams = {
    isExportGrip: true,
    title: this.searchAnalysisTitle,
    limit: this.perPage,
    page: this.page,
    sort: this.sortOrder,
    sortColumn: this.sortColumn,
  };
  public isAllGripAnalysisSelected: boolean = false;
  public selectedGripAnalysisIds: number[] = [];
  public isDomainCategoryRequired: boolean = true;
  public exportOptions: string[] = ['PDF', 'DOCX'];
  public exportType: string = '';
  public isViewSummary: boolean = false;

  @Action('projects/getFilteredGripAnalysisList')
  public getFilteredGripAnalysisList!: (payload: {
    projectId: number;
    params: GripAnalysisParams;
  }) => void;

  @State((state) => state.projects.filteredGripAnalysisList)
  public filteredGripAnalysisList!: Pagination<GripAnalysis>;

  @State((state) => state.projects.apiState.getFilteredGripAnalysisList)
  public getFilteredGripAnalysisListState!: ApiState;

  @Action('projects/getGripExportReport')
  public getGripExportReport!: (payload: GripExportReportPayload) => void;

  @State((state) => state.projects.gripExportReport)
  public gripExportReport!: GripExportReportResponse;

  @State((state) => state.projects.apiState.getGripExportReport)
  public getGripExportReportState!: ApiState;

  @State((state) => state.projects.projectDetail)
  public projectDetail!: ProjectDetailPayload;

  get ExportGripReportModalAnalysisList() {
    return ExportGripReportModalAnalysisList;
  }

  get totalNumberOfPage(): number {
    return Math.ceil(this.totalCount / this.perPage);
  }

  get startItemIndex(): number {
    return this.page * this.perPage - this.perPage + 1;
  }

  get endItemIndex(): number {
    return Math.min(this.totalCount, this.page * this.perPage);
  }

  get isFirstPage(): boolean {
    return this.page === 1;
  }

  get isLastPage(): boolean {
    return this.page === this.totalNumberOfPage;
  }

  get gripAnalysisList(): GripAnalysis[] {
    return this.filteredGripAnalysisList
      ? this.filteredGripAnalysisList.items
      : [];
  }

  get isButtonDisabled(): boolean {
    return !this.exportType.length ? true : false;
  }

  public handleDropdown(): void {
    this.isDropdownActive = !this.isDropdownActive;
  }

  public updateSelectedAnalysis(
    analysisIds: number[],
    isAllSelected: boolean,
  ): void {
    this.selectedGripAnalysisIds = analysisIds;
    this.isAllGripAnalysisSelected = isAllSelected;
  }

  public gripAnalysisCriteria(
    gripAnalysisCriteriaMaps: GripAnalysisCriteria[],
  ): ProjectCriteria[] {
    return gripAnalysisCriteriaMaps.map((criteria) => criteria.criteria);
  }

  public handlePaginator({ perPage, page }: PaginatorDto): void {
    this.page = page;
    this.perPage = perPage;
    this.filteredParams = {
      title: this.searchAnalysisTitle,
      limit: this.perPage,
      page: this.page,
      sort: this.sortOrder,
      sortColumn: this.sortColumn,
      isExportGrip: true,
    };
  }

  public handleSort(data: TableSortDto): void {
    this.sortOrder = data.sortOrder;
    this.sortColumn = data.sortColumn;
    this.filteredParams = {
      title: this.searchAnalysisTitle,
      limit: this.perPage,
      page: this.page,
      sort: this.sortOrder,
      sortColumn: this.sortColumn,
      isExportGrip: true,
    };
  }

  /** Get Filtered Grip Analysis List through API */
  public getFilteredGripAnalysis(): void {
    this.getFilteredGripAnalysisList({
      projectId: this.projectId,
      params: this.filteredParams,
    });
  }

  /** Get Grip Report Data through API */
  public getGripReportData(): void {
    this.getGripExportReport({
      projectId: this.projectId,
      isAllGripAnalysisSelected:
        this.selectedGripAnalysisIds.length > 0
          ? this.isAllGripAnalysisSelected
          : false,
      isDomainCategoryRequired: this.isDomainCategoryRequired,
      gripAnalysisIds: this.selectedGripAnalysisIds,
    });
  }

  public handleViewSummary(): void {
    /** on view summary default export type would be PDF */
    this.exportType = 'PDF';
    this.isViewSummary = true;
    this.getGripReportData();
  }

  public handleExport(): void {
    this.getGripReportData();
  }

  public handleExportToPdf(): void {
    generateGripReportPdf(
      this.isViewSummary,
      this.gripExportReport,
      this.isDomainCategoryRequired,
      this.projectDetail.title,
    );
  }

  public handleExportToDocx(): void {
    generateGripReportDocx(
      this.gripExportReport,
      this.isDomainCategoryRequired,
      this.projectDetail.title,
    );
  }

  public mounted(): void {
    this.getFilteredGripAnalysis();
  }

  @Watch('filteredParams', { deep: true })
  @isDifferentDeep
  public watchFilteredParams(): void {
    this.getFilteredGripAnalysis();
  }

  @Watch('searchAnalysisTitle')
  @Debounce(800)
  public onTitleSearch(): void {
    this.getFilteredGripAnalysis();
  }

  @Watch('getFilteredGripAnalysisListState')
  public watchGetFilteredGripAnalysisListState(state: ApiState): void {
    if (state.loading) {
      this.isLoading = true;
    } else {
      this.isLoading = false;
      if (state.success) {
        this.totalCount = this.filteredGripAnalysisList.totalItems;
        this.selectedGripAnalysisIds = this.isAllGripAnalysisSelected
          ? this.filteredGripAnalysisList.items.map((item) => item.id)
          : this.selectedGripAnalysisIds;
      }
    }
  }

  @Watch('getGripExportReportState')
  public watchGetGripExportReportState(state: ApiState): void {
    if (state.loading) {
      this.isLoading = true;
    } else if (state.success) {
      switch (this.exportType) {
        case 'PDF':
          this.handleExportToPdf();
          break;
        case 'DOCX':
          this.handleExportToDocx();
          break;
        default:
          break;
      }
      if (!this.isViewSummary) {
        this.$emit('close');
      }
      this.isLoading = false;
      this.isViewSummary = false;
    } else {
      this.$emit('close');
      this.isLoading = false;
      this.isViewSummary = false;
    }
  }
}
</script>

<style lang="scss" module>
.modalContainer {
  width: 1140px !important;
}

.selectedCount {
  margin-bottom: 10px;
  color: #9f9f9f;
}
</style>
